import React, { useState, useRef } from 'react';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ImageUpload — Dosya seç, otomatik sıkıştır, yükle, URL döndür
 *
 * Props:
 *  value        — mevcut görsel URL (string)
 *  onChange     — (url: string) => void — yeni URL ile çağırır
 *  token        — JWT auth token
 *  label        — Üstte gösterilen etiket
 *  hint         — Alt açıklama
 *  aspectRatio  — 'square' | 'cover' | 'logo'  (görsel preview oranı)
 *  className    — wrapper className
 */
export default function ImageUpload({
  value = '',
  onChange,
  token,
  label = 'Görsel',
  hint = 'PNG, JPG, WEBP — Maks 20MB',
  aspectRatio = 'square',
  className = '',
  'data-testid': testId,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const aspectClass = {
    square: 'aspect-square',
    cover: 'aspect-[3/1]',
    logo: 'aspect-[2/1]',
  }[aspectRatio] || 'aspect-square';

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Lütfen bir görsel dosyası seçin.');
      return;
    }
    setError('');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.data?.url) {
        onChange(res.data.url);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || 'Yükleme başarısız, tekrar deneyin.';
      setError(typeof msg === 'string' ? msg : 'Yükleme başarısız.');
    } finally {
      setUploading(false);
    }
  };

  const handleFile = (e) => upload(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) upload(file);
  };

  const handleRemove = () => {
    onChange('');
    setError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <div className={`space-y-1.5 ${className}`} data-testid={testId}>
      {label && <label className="block text-sm font-medium text-gray-700">{label}</label>}

      {/* Preview / Drop zone */}
      <div
        className={`relative ${aspectClass} w-full rounded-xl border-2 transition-all cursor-pointer overflow-hidden
          ${dragOver ? 'border-indigo-400 bg-indigo-50' : value ? 'border-gray-200 bg-gray-50' : 'border-dashed border-gray-300 bg-gray-50 hover:border-indigo-400 hover:bg-indigo-50'}`}
        onClick={() => !uploading && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        data-testid={testId ? `${testId}-dropzone` : undefined}
      >
        {value ? (
          <>
            <img
              src={value}
              alt="preview"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {/* Remove + change overlay */}
            <div className="absolute inset-0 bg-black/0 hover:bg-black/40 transition-all flex items-center justify-center opacity-0 hover:opacity-100 gap-2">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
                className="text-white text-xs bg-white/20 border border-white/40 px-3 py-1.5 rounded-lg backdrop-blur-sm font-medium hover:bg-white/30"
              >
                Değiştir
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                className="text-white text-xs bg-red-500/80 px-2 py-1.5 rounded-lg hover:bg-red-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4">
            {uploading ? (
              <>
                <Loader2 className="h-8 w-8 text-indigo-400 animate-spin" />
                <span className="text-xs text-indigo-500 font-medium">Yükleniyor...</span>
              </>
            ) : (
              <>
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-indigo-500" />
                </div>
                <div className="text-center">
                  <span className="text-xs font-semibold text-indigo-600">Dosya seç</span>
                  <span className="text-xs text-gray-400"> veya sürükle</span>
                </div>
                {hint && <span className="text-[11px] text-gray-400 text-center">{hint}</span>}
              </>
            )}
          </div>
        )}

        {/* Upload progress overlay on existing image */}
        {uploading && value && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 text-white animate-spin" />
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <X className="h-3 w-3" />{error}
        </p>
      )}

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
        data-testid={testId ? `${testId}-input` : undefined}
      />
    </div>
  );
}
